import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileCheck, 
  User, 
  DollarSign, 
  Calendar,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  Upload,
  FileText,
  Search,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { FileInput } from "@/components/ui/file-input";
import {
  usePedidosPaginated,
  usePedidosCount,
  aplicarFiltroRecebimentoPedidos,
  fetchDatasPrimeiroRecebimentoPorPedidos,
  fetchDatasRenovacaoPorPedidos,
  usePedidosPrimeiroRecebimento,
  usePedidosDatasRenovacao,
  usePedidosDataFimRenovacao,
  marcarPedidoAguardandoConfirmacao,
  useConfirmarVendaPedido,
  useCancelarConfirmacaoVendaPedido,
  type PedidosSort,
  type PedidosFiltroParams,
} from "@/hooks/usePedidos";
import {
  getStatusListagemLabel,
  getStatusListagemBadgeClass,
  formatDataRenovacaoListagem,
  formatConfirmacaoVenda,
  getConfirmacaoVendaBadgeClass,
} from "@/lib/pedidosCiclo";
import { useOrcamentos } from "@/hooks/useOrcamentos";
import { useCriarPedido } from "@/hooks/usePedidos";
import { useCriarContrato } from "@/hooks/useContratos";
import { useCriarTransacaoFinanceira } from "@/hooks/useFinanceiro";
import { useFormasPagamentoAtivas } from "@/hooks/useFormasPagamento";
import { useUserNamesByIds } from "@/hooks/useUserNamesByIds";
import { useUsuarios } from "@/hooks/useUsuarios";
import { useCidadesPorEstado } from "@/hooks/useClientesEstadosCidades";
import { BRASIL_UFS } from "@/lib/estados";
import { resolverNumeroParcelasGeracao } from "@/lib/orcamentoFormaPagamento";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { preencherPdfContrato, baixarPdfContratoPreenchido } from "@/utils/preencherPdfContrato";
import { gerarParcelasContrato } from "@/hooks/useParcelas";
import { parseDateLocal, toDateStringLocal, proximoDiaUtil } from "@/lib/datas";
import { ListagemAcoes, type ListagemColumn } from "@/components/listagem/ListagemAcoes";
import { listagemThLeft, listagemThCenter, listagemTdLeft, listagemTdCenter } from "@/components/listagem/listagemTableClasses";
import { cn } from "@/lib/utils";
import { usePlanosAtivos } from "@/hooks/usePlanos";
import { getPlanoColor, getPlanoNome } from "@/lib/planoDisplay";
import { uploadFileToStorage, openStorageUrl } from "@/lib/storageUrl";
import { useAuth } from "@/hooks/useAuth";
import { isVendedorScopedRole } from "@/types/auth";
import { applyEscopoVendedorPedidosQuery } from "@/lib/vendedorScope";
import { Textarea } from "@/components/ui/textarea";
import { DetalhesPedidoDialog } from "@/components/DetalhesPedidoDialog";
import { CriarPedidoRenovacaoDialog } from "@/components/CriarPedidoRenovacaoDialog";
import { podeIniciarRenovacao } from "@/lib/renovacaoPrazo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface NovoPedidoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function NovoPedidoDialog({ open, onOpenChange }: NovoPedidoDialogProps) {
  const { data: orcamentosResult } = useOrcamentos();
  const orcamentos = orcamentosResult?.data || [];
  const criarPedido = useCriarPedido();
  const criarContrato = useCriarContrato();
  const criarTransacao = useCriarTransacaoFinanceira();
  const [orcamentoSelecionado, setOrcamentoSelecionado] = useState<number | null>(null);

  // Filtrar apenas orçamentos aprovados que ainda não viraram pedidos
  const orcamentosAprovados = orcamentos.filter(orc => 
    orc.status === 'aprovado'
  );

  console.log('Orçamentos totais:', orcamentos?.length);
  console.log('Orçamentos aprovados:', orcamentosAprovados?.length);
  console.log('Orçamentos aprovados:', orcamentosAprovados);

  const handleCriarPedido = async () => {
    const orcamento = orcamentos.find(o => o.id === orcamentoSelecionado);
    if (!orcamento) {
      console.error('Orçamento não encontrado:', orcamentoSelecionado);
      toast.error("Orçamento não encontrado");
      return;
    }

    console.log('Criando pedido para orçamento:', orcamento);

    try {
      // 1. Criar o pedido
      const pedidoCriado = await criarPedido.mutateAsync({
        orcamento_id: orcamento.id,
        projeto_id: orcamento.projeto_id || 0,
        cliente_id: orcamento.cliente_id || 0,
        valor_total: orcamento.valor_total || 0,
        prazo_entrega: 30
      });

      console.log('Pedido criado com sucesso:', pedidoCriado);

      // 2. Criar contrato automaticamente
      const dataInicio = new Date();
      const dataFim = new Date();
      dataFim.setFullYear(dataFim.getFullYear() + 1); // Contrato por 1 ano

      console.log('Pedido criado com sucesso:', pedidoCriado);

      toast.success("Pedido criado com sucesso! Contrato e financeiro devem ser criados manualmente através dos botões.");
      onOpenChange(false);
      setOrcamentoSelecionado(null);
    } catch (error) {
      console.error('Erro ao criar pedido/contrato/financeiro:', error);
      toast.error("Erro ao criar: " + (error as any)?.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Criar Novo Pedido</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Orçamento Aprovado</label>
            <select 
              value={orcamentoSelecionado || ""}
              onChange={(e) => setOrcamentoSelecionado(e.target.value ? Number(e.target.value) : null)}
              className="w-full mt-1 p-2 border rounded-md"
            >
              <option value="">Selecione um orçamento</option>
              {orcamentosAprovados.map(orcamento => (
                <option key={orcamento.id} value={orcamento.id}>
                  {orcamento.cliente?.nome} - R$ {orcamento.valor_total?.toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCriarPedido}
              disabled={!orcamentoSelecionado || criarPedido.isPending}
            >
              {criarPedido.isPending ? "Criando..." : "Criar Pedido"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Exibe só o primeiro nome na listagem (tooltip guarda o nome completo). */
function primeiroNome(nomeCompleto?: string | null): string {
  const nome = (nomeCompleto || "").trim();
  if (!nome || nome === "—") return "—";
  return nome.split(/\s+/)[0] || "—";
}

export function Pedidos() {
  const queryClient = useQueryClient();
  const { isAdmin, user, profile } = useAuth();
  const isScopedVendedor = isVendedorScopedRole((profile?.user_role || profile?.role) as any) && !!user?.id;
  const confirmarVenda = useConfirmarVendaPedido();
  const cancelarConfirmacao = useCancelarConfirmacaoVendaPedido();
  const [pedidoConfirmar, setPedidoConfirmar] = useState<any>(null);
  const [pedidoCancelar, setPedidoCancelar] = useState<any>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [comprovantePreview, setComprovantePreview] = useState<{
    url: string;
    nome: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [searchTermInput, setSearchTermInput] = useState(''); // Estado local para o input
  const [searchTerm, setSearchTerm] = useState(''); // Estado para a query (com debounce)
  const [sort, setSort] = useState<PedidosSort>(() => ({ field: "created_at", direction: "desc" }));

  const filtrosDraftDefault = useMemo(
    () => ({
      dataInicio: "",
      dataFim: "",
      status: "todos",
      criadoPorId: "",
      planoId: "",
      estado: "",
      cidade: "",
      paineisMin: "",
      paineisMax: "",
      valorProjetoMin: "",
      valorProjetoMax: "",
      valorSeguroMin: "",
      valorSeguroMax: "",
      recebimento: "todos",
      tipoNegocio: "todos",
      confirmacao: "todos",
    }),
    []
  );
  const [filtrosDraft, setFiltrosDraft] = useState(filtrosDraftDefault);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosDraftDefault);
  const { data: usuarios = [] } = useUsuarios();
  const { data: cidadesDoEstado = [] } = useCidadesPorEstado(filtrosDraft.estado);

  // Debounce: atualizar busca após 700ms sem digitação (reduz significativamente requisições ao banco)
  useEffect(() => {
    // Se o campo estiver vazio, limpar busca imediatamente
    if (searchTermInput === "") {
      setSearchTerm("");
      return;
    }

    // Para campos com texto, aguardar delay para evitar queries excessivas
    const timer = setTimeout(() => {
      setSearchTerm(searchTermInput);
    }, 700);

    return () => clearTimeout(timer);
  }, [searchTermInput]);

  const filtrosHook = useMemo<PedidosFiltroParams | null>(() => {
    const toNum = (s: string) => {
      const trimmed = String(s ?? "").trim();
      if (trimmed === "") return undefined;
      const n = Number(trimmed.replace(",", "."));
      return Number.isFinite(n) ? n : undefined;
    };

    const paineisMin = toNum(filtrosAplicados.paineisMin);
    const paineisMax = toNum(filtrosAplicados.paineisMax);
    const valorProjetoMin = toNum(filtrosAplicados.valorProjetoMin);
    const valorProjetoMax = toNum(filtrosAplicados.valorProjetoMax);
    const valorSeguroMin = toNum(filtrosAplicados.valorSeguroMin);
    const valorSeguroMax = toNum(filtrosAplicados.valorSeguroMax);

    return {
      periodo: { dataInicio: filtrosAplicados.dataInicio || undefined, dataFim: filtrosAplicados.dataFim || undefined },
      status: filtrosAplicados.status || "todos",
      criadoPorId: filtrosAplicados.criadoPorId || null,
      planoId: filtrosAplicados.planoId || null,
      estado: filtrosAplicados.estado || null,
      cidade: filtrosAplicados.cidade || null,
      paineis: paineisMin != null || paineisMax != null ? { min: paineisMin, max: paineisMax } : null,
      valorProjeto: valorProjetoMin != null || valorProjetoMax != null ? { min: valorProjetoMin, max: valorProjetoMax } : null,
      valorSeguro: valorSeguroMin != null || valorSeguroMax != null ? { min: valorSeguroMin, max: valorSeguroMax } : null,
      recebimento: (filtrosAplicados.recebimento || "todos") as PedidosFiltroParams["recebimento"],
      tipoNegocio: (filtrosAplicados.tipoNegocio || "todos") as PedidosFiltroParams["tipoNegocio"],
      confirmacao: (filtrosAplicados.confirmacao || "todos") as PedidosFiltroParams["confirmacao"],
      listagem: "ativos",
    } satisfies PedidosFiltroParams;
  }, [filtrosAplicados]);

  const handleAplicarFiltros = () => {
    setFiltrosAplicados(filtrosDraft);
    setCurrentPage(1);
  };

  const handleLimparFiltros = () => {
    setFiltrosDraft(filtrosDraftDefault);
    setFiltrosAplicados(filtrosDraftDefault);
    setSearchTermInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Buscar pedidos paginados com busca + ordenação + filtros (server-side)
  const { data: pedidosResult, isLoading, error } = usePedidosPaginated(
    currentPage,
    itemsPerPage,
    searchTerm,
    sort,
    filtrosHook
  );
  const pedidos = pedidosResult?.data || [];
  const totalPedidos = pedidosResult?.count || 0;
  const { data: formasPagamento = [] } = useFormasPagamentoAtivas();
  const { data: planos = [] } = usePlanosAtivos();
  const { data: orcamentosResult } = useOrcamentos();
  const orcamentos = orcamentosResult?.data || [];
  const [showNovoPedido, setShowNovoPedido] = useState(false);
  const [pedidoSelecionado, setPedidoSelecionado] = useState<number | null>(null);
  const [showNovoContrato, setShowNovoContrato] = useState(false);
  const [showNovoFinanceiro, setShowNovoFinanceiro] = useState(false);
  const [pedidoParaContrato, setPedidoParaContrato] = useState<any>(null);
  const [pedidoParaFinanceiro, setPedidoParaFinanceiro] = useState<any>(null);
  const [numeroParcelasFinanceiro, setNumeroParcelasFinanceiro] = useState<number>(1);
  const [arquivoContrato, setArquivoContrato] = useState<File | null>(null);
  const [showDetalhesPedido, setShowDetalhesPedido] = useState(false);
  const [pedidoDetalhesId, setPedidoDetalhesId] = useState<number | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [etapaContrato, setEtapaContrato] = useState<'confirmar' | 'upload'>('confirmar');
  const [pdfGerado, setPdfGerado] = useState<Blob | null>(null);
  
  // Resetar para página 1 quando busca, filtros ou ordenação mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filtrosAplicados, sort.field, sort.direction]);

  const toggleSort = (field: PedidosSort["field"]) => {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field, direction: "asc" };
    });
  };
  
  // Os pedidos já vêm filtrados do backend
  const filteredPedidos = pedidos;
  
  // Calcular dados da paginação
  const totalItems = totalPedidos;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems = filteredPedidos; // Já vem paginado e filtrado do backend

  const { data: userNamesMap } = useUserNamesByIds(
    currentItems.flatMap((p: any) => [
      p.vendedor_id,
      p.orcamento?.vendedor_id,
      p.confirmado_por,
      p.confirmacao_cancelado_por,
    ])
  );

  const pedidoIdsPagina = useMemo(
    () => (currentItems as { id: number }[]).map((p) => p.id),
    [currentItems]
  );
  const { data: datasPrimeiroRecebimento = {} } = usePedidosPrimeiroRecebimento(pedidoIdsPagina);
  const { data: datasRenovacao = {} } = usePedidosDatasRenovacao(pedidoIdsPagina);
  const { data: datasFimRenovacao = {} } = usePedidosDataFimRenovacao(pedidoIdsPagina);
  const [pedidoRenovacaoId, setPedidoRenovacaoId] = useState<number | null>(null);

  const formatDataPrimeiroRecebimento = (pedidoId: number) => {
    const raw = datasPrimeiroRecebimento[pedidoId];
    if (!raw) return "—";
    return new Date(`${raw}T12:00:00`).toLocaleDateString("pt-BR");
  };

  const criarContrato = useCriarContrato();
  const criarTransacao = useCriarTransacaoFinanceira();

  // Resetar página quando o termo de busca mudar (já está no useEffect acima)

  const uploadArquivoContrato = async (file: File, contratoId: number) => {
    try {
      console.log(`Iniciando upload do arquivo do contrato ${contratoId}`);
      
      // Verificar se o arquivo existe
      if (!file) {
        throw new Error('Arquivo não fornecido');
      }

      // Verificar tamanho do arquivo (máximo 20MB)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        throw new Error('Arquivo muito grande. Máximo 20MB permitido.');
      }

      // Verificar se é PDF
      if (!file.type.includes('pdf')) {
        throw new Error('Apenas arquivos PDF são permitidos.');
      }

      const timestamp = Date.now();
      const fileName = `contrato_${contratoId}_${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `contratos/${contratoId}/${fileName}`;
      const { publicUrl } = await uploadFileToStorage(filePath, file);

      // Atualizar o contrato com a URL do arquivo
      const { error: updateError } = await supabase
        .from('contratos')
        .update({
          arquivo_pdf_url: publicUrl,
          arquivo_pdf_name: file.name
        })
        .eq('id', contratoId);

      if (updateError) {
        console.error('Erro ao atualizar contrato com URL do arquivo:', updateError);
        throw updateError;
      }

      console.log('Arquivo do contrato enviado com sucesso:', publicUrl);
      return { publicUrl, fileName: file.name };
      
    } catch (error) {
      console.error('Erro ao fazer upload do arquivo do contrato:', error);
      throw error;
    }
  };

  const handleCriarContrato = async (pedido: any) => {
    try {
      console.log('🔍 Buscando dados completos para o contrato...');
      
      // Buscar dados completos do banco
      // 1. Buscar cliente completo
      const { data: clienteCompleto, error: errorCliente } = await supabase
        .from('clientes')
        .select('*')
        .eq('id', pedido.cliente_id)
        .single();
      
      if (errorCliente) {
        console.error('Erro ao buscar cliente:', errorCliente);
      }
      
      // 2. Buscar equipamentos do orçamento ou projeto
      let equipamentos: any[] = [];
      if (pedido.orcamento_id) {
        // Tentar buscar equipamentos do orçamento primeiro
        const { data: equipamentosOrcamento } = await supabase
          .from('orcamento_equipamentos')
          .select(`
            *,
            equipamento:equipamentos(*)
          `)
          .eq('orcamento_id', pedido.orcamento_id);
        
        if (equipamentosOrcamento && equipamentosOrcamento.length > 0) {
          equipamentos = equipamentosOrcamento;
        } else if (pedido.projeto_id) {
          // Se não encontrou no orçamento, buscar no projeto
          const { data: equipamentosProjeto } = await supabase
            .from('projeto_equipamentos')
            .select(`
              *,
              equipamento:equipamentos(*)
            `)
            .eq('projeto_id', pedido.projeto_id);
          
          if (equipamentosProjeto) {
            equipamentos = equipamentosProjeto;
          }
        }
      } else if (pedido.projeto_id) {
        // Buscar direto do projeto
        const { data: equipamentosProjeto } = await supabase
          .from('projeto_equipamentos')
          .select(`
            *,
            equipamento:equipamentos(*)
          `)
          .eq('projeto_id', pedido.projeto_id);
        
        if (equipamentosProjeto) {
          equipamentos = equipamentosProjeto;
        }
      }
      
      // 3. Buscar plano completo se tiver plano_id
      let planoCompleto: any = null;
      if (pedido.orcamento?.plano_id) {
        const { data: plano } = await supabase
          .from('planos_seguro')
          .select('*')
          .eq('id', pedido.orcamento.plano_id)
          .single();
        
        if (plano) {
          planoCompleto = {
            ...plano,
            vigencia_anos: Math.floor(plano.vigencia_meses / 12) || 1,
          };
        }
      }
      
      // 4. Buscar orçamento completo se necessário
      let orcamentoCompleto = pedido.orcamento;
      if (pedido.orcamento_id && !orcamentoCompleto?.forma_pagamento) {
        const { data: orcamento } = await supabase
          .from('orcamentos')
          .select('*')
          .eq('id', pedido.orcamento_id)
          .single();
        
        if (orcamento) {
          orcamentoCompleto = orcamento;
        }
      }
      
      // Usar valor do seguro para o contrato
      const valorContrato = orcamentoCompleto?.valor_seguro || pedido.valor_total || 0;
      
      // Calcular data final baseada na vigência do plano
      const dataInicio = new Date();
      const vigenciaAnos = planoCompleto?.vigencia_anos || orcamentoCompleto?.vigencia_anos || 1;
      const dataFim = new Date(dataInicio);
      dataFim.setFullYear(dataFim.getFullYear() + vigenciaAnos);
      dataFim.setDate(dataFim.getDate() - 1); // Subtrair 1 dia para ser exatamente a vigência
      
      console.log('✅ Dados completos buscados:', {
        cliente: clienteCompleto?.nome,
        cliente_cpf_cnpj: clienteCompleto?.cpf_cnpj,
        cliente_completo: clienteCompleto,
        equipamentos_count: equipamentos.length,
        equipamentos: equipamentos,
        plano: planoCompleto?.nome,
        vigencia_anos: vigenciaAnos,
        valor_contrato: valorContrato,
        orcamento_completo: orcamentoCompleto
      });
      
      const pedidoComDados = { 
        ...pedido, 
        cliente: clienteCompleto || pedido.cliente,
        orcamento: {
          ...(orcamentoCompleto || pedido.orcamento),
          equipamentos: equipamentos,
          plano: planoCompleto,
        },
        projeto: {
          ...(pedido.projeto || {}),
          equipamentos: equipamentos,
        },
        valor_contrato: valorContrato,
        data_inicio: dataInicio.toISOString().split('T')[0],
        data_fim: dataFim.toISOString().split('T')[0],
        vigencia_anos: vigenciaAnos
      };
      
      // Gerar PDF automaticamente ao abrir o modal
      console.log('Gerando PDF do contrato automaticamente...');
      
      // Preparar dados para o PDF
      const dadosContrato = {
        cliente: {
          nome: clienteCompleto?.nome || pedido.cliente?.nome || `Cliente ID: ${pedido.cliente_id}`,
          cpf_cnpj: clienteCompleto?.cpf_cnpj || pedido.cliente?.cpf_cnpj || '',
          endereco: clienteCompleto?.endereco || pedido.cliente?.endereco || '',
          numero: clienteCompleto?.numero || pedido.cliente?.numero || '',
          bairro: clienteCompleto?.bairro || pedido.cliente?.bairro || '',
          cidade: clienteCompleto?.cidade || pedido.cliente?.cidade || '',
          estado: clienteCompleto?.estado || pedido.cliente?.estado || '',
          cep: clienteCompleto?.cep || pedido.cliente?.cep || '',
          telefone: clienteCompleto?.telefone || pedido.cliente?.telefone || '',
          email: clienteCompleto?.email || pedido.cliente?.email || '',
        },
        equipamentos: equipamentos,
        plano: planoCompleto || {
          nome: orcamentoCompleto?.plano_id || 'Não informado',
          tipo_plano: orcamentoCompleto?.plano_id || 'Não informado',
          vigencia_anos: vigenciaAnos,
        },
        orcamento: {
          quantidade_placas: orcamentoCompleto?.quantidade_placas || 0,
          valor_seguro: orcamentoCompleto?.valor_seguro || 0,
          valor_total: orcamentoCompleto?.valor_total || 0,
          forma_pagamento: orcamentoCompleto?.forma_pagamento,
        },
        projeto: pedido.projeto || {},
        contrato: {
          data_inicio: pedidoComDados.data_inicio,
          data_fim: pedidoComDados.data_fim,
          valor: valorContrato,
        },
      };
      
      console.log('📄 Dados preparados para o PDF:', dadosContrato);

      // Gerar PDF
      const pdfBlob = await preencherPdfContrato(dadosContrato);
      
      // Converter Blob para File
      const pdfFile = new File([pdfBlob], `${pedido.cliente?.nome?.replace(/[^a-zA-Z0-9]/g, '_') || 'CONTRATO'}_${new Date().toISOString().split('T')[0]}.pdf`, {
        type: 'application/pdf',
      });
      
      // Criar URL para preview do PDF
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Configurar estados para o modal
      setPdfPreviewUrl(pdfUrl);
      setPdfGerado(pdfBlob);
      setPedidoParaContrato(pedidoComDados);
      setEtapaContrato('confirmar'); // Começar na etapa de confirmação/download
      setArquivoContrato(null); // Limpar arquivo assinado (será feito upload depois)
      setShowNovoContrato(true);
      
      toast.success("PDF do contrato gerado! Revise as informações, faça o download e depois faça o upload do arquivo assinado.");
    } catch (error) {
      console.error('Erro ao gerar PDF do contrato:', error);
      toast.error(`Erro ao gerar PDF: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  };

  const handleCriarFinanceiro = async (pedido: any) => {
    try {
      // Evitar duplicidade: já existem transações financeiras para este pedido?
      const { data: transacoesExistentes } = await supabase
        .from('transacoes_financeiras')
        .select('id')
        .eq('pedido_id', pedido.id)
        .limit(1);

      if (transacoesExistentes && transacoesExistentes.length > 0) {
        toast.info("Já existem transações financeiras para este pedido. Verifique na seção Financeiro.");
        return;
      }

      // Abrir modal: financeiro é independente do contrato/parcelas
      const valorFinanceiro = pedido.orcamento?.valor_seguro || pedido.valor_total || 0;
      const hoje = new Date();
      const ano = hoje.getFullYear();
      const mes = String(hoje.getMonth() + 1).padStart(2, '0');
      const dia = String(hoje.getDate()).padStart(2, '0');
      const dataVencimentoFormatada = `${ano}-${mes}-${dia}`;

      let formaPagamentoNome = '';
      let numeroParcelasInicial = 1;
      if (pedido.orcamento?.forma_pagamento) {
        const { data: formaData } = await supabase
          .from('formas_pagamento')
          .select('nome, tipo, parcelas_max')
          .eq('id', pedido.orcamento.forma_pagamento)
          .single();
        if (formaData?.nome) formaPagamentoNome = formaData.nome;
        numeroParcelasInicial = resolverNumeroParcelasGeracao({
          numeroParcelasOrcamento: pedido.orcamento?.numero_parcelas,
          parcelasCartaoOrcamento: pedido.orcamento?.parcelas_cartao,
          parcelasMaxForma: formaData?.parcelas_max,
          isCartao: formaData?.tipo === 'cartao',
          formaNome: formaData?.nome,
        });
      }

      setNumeroParcelasFinanceiro(numeroParcelasInicial);
      setPedidoParaFinanceiro({
        ...pedido,
        valor_financeiro: valorFinanceiro,
        data_vencimento: dataVencimentoFormatada,
        forma_pagamento: formaPagamentoNome || pedido.orcamento?.forma_pagamento,
        observacoes: "",
      });
      setShowNovoFinanceiro(true);
    } catch (error: any) {
      console.error('Erro ao abrir modal financeiro:', error);
      toast.error(`Erro ao gerar financeiro: ${error?.message || 'Erro desconhecido'}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aprovado': return 'bg-green-100 text-green-800';
      case 'em_contrato': return 'bg-yellow-100 text-yellow-800';
      case 'finalizado': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'aprovado': return <CheckCircle className="h-4 w-4" />;
      case 'em_contrato': return <Clock className="h-4 w-4" />;
      case 'finalizado': return <FileCheck className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  type PedidoRow = (typeof currentItems extends (infer T)[] ? T : any) & {
    numero_formatado: string;
    criado_por: string;
    paineis_info: string;
    valor_projeto_info: string;
    valor_seguro: string;
    data_aprovacao: string;
    data_primeiro_recebimento: string;
    cliente_nome: string;
    cliente_localizacao: string;
    status_listagem: string;
    data_renovacao_label: string;
    plano_label: string;
  };
  const pedidoColumns: ListagemColumn<PedidoRow>[] = [
    { key: "numero", label: "Número", value: (p) => p.numero_formatado },
    { key: "cliente", label: "Cliente", value: (p) => p.cliente_nome },
    { key: "localizacao", label: "Estado/Cidade", value: (p) => p.cliente_localizacao },
    { key: "criado_por", label: "Criado por", value: (p) => p.criado_por },
    { key: "status", label: "Status", value: (p) => p.status_listagem },
    {
      key: "data_renovacao",
      label: "Data renovação",
      value: (p) => p.data_renovacao_label,
    },
    { key: "paineis", label: "Painéis", value: (p) => p.paineis_info },
    { key: "plano", label: "Plano", value: (p) => p.plano_label },
    { key: "valor_projeto", label: "Valor do Projeto (Info)", value: (p) => p.valor_projeto_info },
    { key: "valor_seguro", label: "Valor do Seguro", value: (p) => p.valor_seguro },
    { key: "data_aprovacao", label: "Data Aprovação", value: (p) => p.data_aprovacao },
    {
      key: "data_primeiro_recebimento",
      label: "1º Recebimento",
      value: (p) => p.data_primeiro_recebimento ?? formatDataPrimeiroRecebimento(p.id),
    },
    {
      key: "confirmacao",
      label: "Confirmação",
      value: (p) => formatConfirmacaoVenda(p.confirmacao_status),
    },
  ];
  const [visiblePedidoCols, setVisiblePedidoCols] = useState<string[]>(pedidoColumns.map((c) => c.key));
  const togglePedidoCol = (key: string, nextVisible: boolean) => {
    setVisiblePedidoCols((prev) => (nextVisible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Carregando pedidos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <AlertCircle className="h-8 w-8 mx-auto" />
          </div>
          <p className="text-red-600">Erro ao carregar pedidos</p>
          <p className="text-sm text-gray-600 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Pedidos</h2>
          <p className="text-gray-600">Primeira venda e renovações ativas (não renovados ficam em arquivo separado)</p>
        </div>
        <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => window.dispatchEvent(new CustomEvent("mudar_secao", { detail: "pedidos_nao_renovados" }))}
        >
          Não renovados
        </Button>
        <Button onClick={() => setShowNovoPedido(true)} className="bg-primary hover:bg-primary/90">
          <FileCheck className="h-4 w-4 mr-2" />
          Novo Pedido
        </Button>
        </div>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-gray-900">Lista de Pedidos ({totalPedidos})</CardTitle>
            <ListagemAcoes
              title="Pedidos"
              subtitle={`Ordenação: ${sort.field} ${sort.direction} • Página ${currentPage}/${totalPages}`}
              rows={currentItems.map((pedido: any) => {
                const numero_formatado = `#${pedido.id.toString().padStart(6, "0")}`;
                const criado_por =
                  (pedido.vendedor_id || pedido.orcamento?.vendedor_id)
                    ? (userNamesMap?.[pedido.vendedor_id ?? ""] || userNamesMap?.[pedido.orcamento?.vendedor_id ?? ""] || "—")
                    : "—";
                const paineis_info =
                  typeof pedido.projeto_id === "number"
                    ? String(pedido.projeto?.total_paineis ?? 0)
                    : "-";
                const valor_projeto_info = (() => {
                  if (pedido.projeto?.valor_projeto && pedido.projeto.valor_projeto > 0) {
                    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(pedido.projeto.valor_projeto));
                  }
                  if (pedido.orcamento?.valor_total) {
                    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(pedido.orcamento.valor_total));
                  }
                  if (pedido.valor_total) {
                    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(pedido.valor_total));
                  }
                  return '-';
                })();
                const valor_seguro = pedido.orcamento?.valor_seguro && pedido.orcamento.valor_seguro > 0
                  ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(pedido.orcamento.valor_seguro))
                  : '-';
                const data_aprovacao = pedido.data_pedido ? new Date(pedido.data_pedido).toLocaleDateString('pt-BR') : '-';
                const cliente_nome = pedido.cliente?.nome || `Cliente ID: ${pedido.cliente_id}`;
                const uf = (pedido.cliente?.estado || "—").trim();
                const city = (pedido.cliente?.cidade || "—").trim();
                const cliente_localizacao = `${uf} / ${city}`;
                const dataRenov =
                  datasRenovacao[pedido.id] ||
                  pedido.data_renovacao ||
                  (pedido.tipo_negocio === "renovacao" ? pedido.data_pedido : null);
                const planoId = pedido.orcamento?.plano_id;
                const plano_label = planoId != null ? getPlanoNome(planoId, planos) : "-";
                return {
                  ...pedido,
                  numero_formatado,
                  criado_por,
                  paineis_info,
                  plano_label,
                  valor_projeto_info,
                  valor_seguro,
                  data_aprovacao,
                  data_primeiro_recebimento: formatDataPrimeiroRecebimento(pedido.id),
                  cliente_nome,
                  cliente_localizacao,
                  status_listagem: getStatusListagemLabel(pedido.tipo_negocio, pedido.numero_ciclo),
                  data_renovacao_label: formatDataRenovacaoListagem(dataRenov),
                } as PedidoRow;
              })}
              fetchAllRows={async () => {
                const batch = 1000;
                let from = 0;
                const all: any[] = [];

                const userNameById = (id?: string | null) => {
                  if (!id) return "—";
                  const u = (usuarios || []).find((x: any) => x.id === id);
                  return u?.nome || u?.email || "—";
                };

                const projetoFilterPresent =
                  (filtrosHook?.paineis?.min ?? null) !== null ||
                  (filtrosHook?.paineis?.max ?? null) !== null ||
                  (filtrosHook?.valorProjeto?.min ?? null) !== null ||
                  (filtrosHook?.valorProjeto?.max ?? null) !== null;
                const orcamentoFilterPresent =
                  (filtrosHook?.valorSeguro?.min ?? null) !== null ||
                  (filtrosHook?.valorSeguro?.max ?? null) !== null ||
                  !!filtrosHook?.planoId;
                const clienteFilterPresent = !!(filtrosHook?.estado || filtrosHook?.cidade);

                const clienteEmbed = `cliente:clientes${clienteFilterPresent ? "!inner" : ""}(id, nome, email, telefone, cidade, estado)`;
                const projetoEmbed = `projeto:projetos${projetoFilterPresent ? "!inner" : ""}(id, valor_projeto, total_paineis, observacoes, status)`;
                // !orcamento_id evita ambiguidade com orcamentos.pedido_origem_id
                const orcamentoEmbed = `orcamento:orcamentos!orcamento_id${orcamentoFilterPresent ? "!inner" : ""}(id, valor_total, valor_seguro, quantidade_placas, vigencia_anos, plano_id, forma_pagamento, parcelas_cartao, numero_parcelas, vendedor_id)`;

                let base = supabase
                  .from("pedidos")
                  .select(
                    `
                      *,
                      ${clienteEmbed},
                      ${orcamentoEmbed},
                      ${projetoEmbed}
                    `
                  );

                const listagem = filtrosHook?.listagem ?? "ativos";
                if (listagem === "nao_renovados") {
                  base = base.eq("situacao_renovacao", "nao_renovado");
                } else {
                  base = base.in("situacao_renovacao", ["em_vigencia", "aguardando_renovacao"]);
                }
                if (filtrosHook?.tipoNegocio && filtrosHook.tipoNegocio !== "todos") {
                  base = base.eq("tipo_negocio", filtrosHook.tipoNegocio);
                }

                if (filtrosHook?.periodo?.dataInicio) base = base.gte("data_pedido", filtrosHook.periodo.dataInicio);
                if (filtrosHook?.periodo?.dataFim) base = base.lte("data_pedido", filtrosHook.periodo.dataFim);
                if (filtrosHook?.status && filtrosHook.status !== "todos") base = base.eq("status", filtrosHook.status);
                if (filtrosHook?.criadoPorId) {
                  base = base.eq("vendedor_id", filtrosHook.criadoPorId);
                } else if (isScopedVendedor) {
                  const scoped = await applyEscopoVendedorPedidosQuery(base, user!.id);
                  base = scoped.query;
                }
                if (filtrosHook?.planoId) base = base.eq("orcamento.plano_id", filtrosHook.planoId);
                if (filtrosHook?.estado) base = base.eq("cliente.estado", filtrosHook.estado.toUpperCase());
                if (filtrosHook?.cidade) base = base.eq("cliente.cidade", filtrosHook.cidade);

                if (typeof filtrosHook?.paineis?.min === "number") base = base.gte("projeto.total_paineis", filtrosHook.paineis.min);
                if (typeof filtrosHook?.paineis?.max === "number") base = base.lte("projeto.total_paineis", filtrosHook.paineis.max);
                if (typeof filtrosHook?.valorProjeto?.min === "number") base = base.gte("projeto.valor_projeto", filtrosHook.valorProjeto.min);
                if (typeof filtrosHook?.valorProjeto?.max === "number") base = base.lte("projeto.valor_projeto", filtrosHook.valorProjeto.max);
                if (typeof filtrosHook?.valorSeguro?.min === "number") base = base.gte("orcamento.valor_seguro", filtrosHook.valorSeguro.min);
                if (typeof filtrosHook?.valorSeguro?.max === "number") base = base.lte("orcamento.valor_seguro", filtrosHook.valorSeguro.max);

                const recvExport = await aplicarFiltroRecebimentoPedidos(
                  base,
                  filtrosHook?.recebimento ?? "todos"
                );
                if (recvExport.empty) return [];
                base = recvExport.query;

                if (searchTerm && searchTerm.trim()) {
                  const searchLower = searchTerm.trim();
                  if (!isNaN(Number(searchLower))) {
                    base = base.eq("id", parseInt(searchLower));
                  } else {
                    const { data: clientesEncontrados } = await supabase
                      .from("clientes")
                      .select("id")
                      .ilike("nome", `%${searchLower}%`);
                    if (clientesEncontrados && clientesEncontrados.length > 0) {
                      const clienteIds = clientesEncontrados.map((c: any) => c.id);
                      base = base.or(`numero_pedido.ilike.%${searchLower}%,status.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(",")})`);
                    } else {
                      base = base.or(`numero_pedido.ilike.%${searchLower}%,status.ilike.%${searchLower}%`);
                    }
                  }
                }

                const sortField = sort?.field || "created_at";
                const sortAsc = (sort?.direction || "desc") === "asc";

                while (true) {
                  let q = base;
                  if (sortField === "cliente_nome") {
                    q = q.order("nome", { ascending: sortAsc, foreignTable: "cliente" });
                  } else if (sortField === "valor_seguro") {
                    q = q.order("valor_seguro", { ascending: sortAsc, foreignTable: "orcamento" });
                  } else if (sortField === "paineis") {
                    q = q.order("total_paineis", { ascending: sortAsc, foreignTable: "projeto" });
                  } else {
                    q = q.order(sortField, { ascending: sortAsc });
                  }
                  q = q.range(from, from + batch - 1);

                  const { data, error } = await q;
                  if (error) throw error;
                  const rows = data || [];
                  all.push(...rows);
                  if (rows.length < batch) break;
                  from += batch;
                }

                const exportIds = all.map((p: { id: number }) => p.id);
                const datasExport = await fetchDatasPrimeiroRecebimentoPorPedidos(exportIds);
                const datasRenovExport = await fetchDatasRenovacaoPorPedidos(exportIds);
                const fmtReceb = (id: number) => {
                  const raw = datasExport[id];
                  if (!raw) return "—";
                  return new Date(`${raw}T12:00:00`).toLocaleDateString("pt-BR");
                };

                return all.map((pedido: any) => {
                  const numero_formatado = `#${pedido.id.toString().padStart(6, "0")}`;
                  const criado_por = userNameById(pedido.vendedor_id ?? pedido.orcamento?.vendedor_id);
                  const paineis_info =
                    typeof pedido.projeto_id === "number" ? String(pedido.projeto?.total_paineis ?? 0) : "-";
                  const valor_projeto_info = (() => {
                    if (pedido.projeto?.valor_projeto && pedido.projeto.valor_projeto > 0) {
                      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(pedido.projeto.valor_projeto));
                    }
                    if (pedido.orcamento?.valor_total) {
                      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(pedido.orcamento.valor_total));
                    }
                    if (pedido.valor_total) {
                      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(pedido.valor_total));
                    }
                    return "-";
                  })();
                  const valor_seguro =
                    pedido.orcamento?.valor_seguro && pedido.orcamento.valor_seguro > 0
                      ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(pedido.orcamento.valor_seguro))
                      : "-";
                  const data_aprovacao = pedido.data_pedido ? new Date(pedido.data_pedido).toLocaleDateString("pt-BR") : "-";
                  const cliente_nome = pedido.cliente?.nome || `Cliente ID: ${pedido.cliente_id}`;
                  const uf = (pedido.cliente?.estado || "—").trim();
                  const city = (pedido.cliente?.cidade || "—").trim();
                  const cliente_localizacao = `${uf} / ${city}`;
                  const dataRenov =
                    datasRenovExport[pedido.id] ||
                    pedido.data_renovacao ||
                    (pedido.tipo_negocio === "renovacao" ? pedido.data_pedido : null);
                  const planoId = pedido.orcamento?.plano_id;
                  const plano_label = planoId != null ? getPlanoNome(planoId, planos) : "-";
                  return {
                    ...pedido,
                    numero_formatado,
                    criado_por,
                    paineis_info,
                    plano_label,
                    valor_projeto_info,
                    valor_seguro,
                    data_aprovacao,
                    data_primeiro_recebimento: fmtReceb(pedido.id),
                    cliente_nome,
                    cliente_localizacao,
                    status_listagem: getStatusListagemLabel(pedido.tipo_negocio, pedido.numero_ciclo),
                    data_renovacao_label: formatDataRenovacaoListagem(dataRenov),
                  } as PedidoRow;
                });
              }}
              columns={pedidoColumns}
              visibleKeys={visiblePedidoCols}
              onToggleColumn={togglePedidoCol}
              filenameBase={`pedidos_filtrados`}
            />
          </div>

          <div className="mt-4 bg-[#C9C9C9] rounded-lg p-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-3 items-end">
              {/* Linha 1 */}
              <div className="md:col-span-4">
                <Label className="text-xs">Buscar</Label>
                <Input
                  placeholder="Buscar por número, cliente, status, valor..."
                  value={searchTermInput}
                  onChange={(e) => setSearchTermInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setSearchTerm(searchTermInput);
                    }
                  }}
                  className="h-8 text-xs"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Período - início</Label>
                <Input type="date" value={filtrosDraft.dataInicio} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, dataInicio: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Período - fim</Label>
                <Input type="date" value={filtrosDraft.dataFim} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, dataFim: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Status</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.status}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, status: e.target.value })}
                >
                  <option value="todos">Todos</option>
                  <option value="aprovado">Aprovado</option>
                  <option value="em_contrato">Em contrato</option>
                  <option value="finalizado">Finalizado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Criado por</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.criadoPorId}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, criadoPorId: e.target.value })}
                >
                  <option value="">Todos</option>
                  {(usuarios || [])
                    .filter((u: any) => u?.ativo !== false)
                    .map((u: any) => (
                      <option key={u.id} value={u.id}>
                        {u.nome || u.email}
                      </option>
                    ))}
                </select>
              </div>

              {/* Linha 2 — 6 filtros */}
              <div className="md:col-span-2">
                <Label className="text-xs">Tipo de plano</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.planoId}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, planoId: e.target.value })}
                >
                  <option value="">Todos</option>
                  {(planos || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Estado</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.estado}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, estado: e.target.value, cidade: "" })}
                >
                  <option value="">Todos</option>
                  {BRASIL_UFS.map((uf) => (
                    <option key={uf.sigla} value={uf.sigla}>
                      {uf.sigla} - {uf.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Cidade</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs disabled:opacity-60"
                  value={filtrosDraft.cidade}
                  onChange={(e) => setFiltrosDraft({ ...filtrosDraft, cidade: e.target.value })}
                  disabled={!filtrosDraft.estado}
                >
                  <option value="">{filtrosDraft.estado ? "Todas" : "Selecione um estado"}</option>
                  {(cidadesDoEstado || []).map((c: string) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Painéis mín</Label>
                <Input type="number" inputMode="numeric" step="1" placeholder="Mín" value={filtrosDraft.paineisMin} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, paineisMin: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Painéis máx</Label>
                <Input type="number" inputMode="numeric" step="1" placeholder="Máx" value={filtrosDraft.paineisMax} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, paineisMax: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Confirmação</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.confirmacao}
                  onChange={(e) =>
                    setFiltrosDraft({
                      ...filtrosDraft,
                      confirmacao: e.target.value as
                        | "todos"
                        | "aguardando"
                        | "confirmada"
                        | "cancelada"
                        | "sem_financeiro",
                    })
                  }
                >
                  <option value="todos">Todos</option>
                  <option value="aguardando">Aguardando</option>
                  <option value="confirmada">Confirmada</option>
                  <option value="cancelada">Cancelada</option>
                  <option value="sem_financeiro">Sem financeiro</option>
                </select>
              </div>

              {/* Linha 3 — 6 filtros */}
              <div className="md:col-span-2">
                <Label className="text-xs">Valor projeto mín</Label>
                <Input type="number" inputMode="decimal" step="0.01" placeholder="Mín" value={filtrosDraft.valorProjetoMin} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorProjetoMin: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Valor projeto máx</Label>
                <Input type="number" inputMode="decimal" step="0.01" placeholder="Máx" value={filtrosDraft.valorProjetoMax} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorProjetoMax: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Valor seguro mín</Label>
                <Input type="number" inputMode="decimal" step="0.01" placeholder="Mín" value={filtrosDraft.valorSeguroMin} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorSeguroMin: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Valor seguro máx</Label>
                <Input type="number" inputMode="decimal" step="0.01" placeholder="Máx" value={filtrosDraft.valorSeguroMax} onChange={(e) => setFiltrosDraft({ ...filtrosDraft, valorSeguroMax: e.target.value })} className="h-8 text-xs" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Recebimento</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.recebimento}
                  onChange={(e) =>
                    setFiltrosDraft({
                      ...filtrosDraft,
                      recebimento: e.target.value as "todos" | "recebido" | "nao_recebido",
                    })
                  }
                  title="1ª parcela ou à vista recebida — quando o seguro pode valer"
                >
                  <option value="todos">Todos</option>
                  <option value="recebido">Recebido</option>
                  <option value="nao_recebido">Não recebido</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Tipo de negócio</Label>
                <select
                  className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                  value={filtrosDraft.tipoNegocio}
                  onChange={(e) =>
                    setFiltrosDraft({
                      ...filtrosDraft,
                      tipoNegocio: e.target.value as "todos" | "primeira_venda" | "renovacao",
                    })
                  }
                >
                  <option value="todos">Todos</option>
                  <option value="primeira_venda">Primeira venda</option>
                  <option value="renovacao">Renovação</option>
                </select>
              </div>

              <div className="md:col-span-12 flex items-end justify-end gap-2 pt-1">
                <Button variant="outline" type="button" onClick={handleLimparFiltros} className="h-8 px-3 text-xs">
                  Limpar
                </Button>
                <Button type="button" className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-3 text-xs" onClick={handleAplicarFiltros}>
                  Aplicar
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="w-full overflow-x-auto">
            <table className="w-full table-fixed text-[12px] leading-snug">
              <thead>
                <tr className="border-b">
                  {visiblePedidoCols.includes("numero") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap px-1.5 py-2 w-[4.5%]")}
                      onClick={() => toggleSort("id")}
                      title="Ordenar"
                    >
                      Nº {sort.field === "id" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visiblePedidoCols.includes("cliente") && (
                    <th
                      className={cn(listagemThLeft, "cursor-pointer select-none px-1.5 py-2 w-[16%]")}
                      onClick={() => toggleSort("cliente_nome")}
                      title="Ordenar"
                    >
                      Cliente {sort.field === "cliente_nome" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visiblePedidoCols.includes("localizacao") && (
                    <th className={cn(listagemThCenter, "hidden md:table-cell px-1.5 py-2 w-[11%]")}>
                      UF/Cidade
                    </th>
                  )}
                  {visiblePedidoCols.includes("criado_por") && (
                    <th className={cn(listagemThLeft, "hidden lg:table-cell px-1.5 py-2 w-[6%]")}>Criado por</th>
                  )}
                  {visiblePedidoCols.includes("status") && (
                    <th className={cn(listagemThCenter, "whitespace-nowrap px-1.5 py-2 w-[7%]")}>
                      Status
                    </th>
                  )}
                  {visiblePedidoCols.includes("data_renovacao") && (
                    <th className={cn(listagemThCenter, "whitespace-nowrap hidden md:table-cell px-1.5 py-2 w-[5%]")}>
                      Renovação
                    </th>
                  )}
                  {visiblePedidoCols.includes("paineis") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden md:table-cell px-1.5 py-2 w-[4%]")}
                      onClick={() => toggleSort("paineis")}
                      title="Ordenar"
                    >
                      Painéis {sort.field === "paineis" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visiblePedidoCols.includes("plano") && (
                    <th className={cn(listagemThCenter, "hidden md:table-cell px-1.5 py-2 w-[9%]")}>
                      Plano
                    </th>
                  )}
                  {visiblePedidoCols.includes("valor_projeto") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none hidden lg:table-cell px-1.5 py-2 w-[5.5%]")}
                      onClick={() => toggleSort("valor_total")}
                      title="Valor do Projeto (Info)"
                    >
                      Vlr. Projeto {sort.field === "valor_total" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visiblePedidoCols.includes("valor_seguro") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none hidden lg:table-cell px-1.5 py-2 w-[5.5%]")}
                      onClick={() => toggleSort("valor_seguro")}
                      title="Valor do Seguro"
                    >
                      Vlr. Seguro {sort.field === "valor_seguro" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visiblePedidoCols.includes("data_aprovacao") && (
                    <th
                      className={cn(listagemThCenter, "cursor-pointer select-none whitespace-nowrap hidden md:table-cell px-1.5 py-2 w-[5%]")}
                      onClick={() => toggleSort("data_pedido")}
                      title="Data Aprovação"
                    >
                      Aprovação {sort.field === "data_pedido" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                    </th>
                  )}
                  {visiblePedidoCols.includes("data_primeiro_recebimento") && (
                    <th
                      className={cn(listagemThCenter, "whitespace-nowrap hidden md:table-cell px-1.5 py-2 w-[5%]")}
                      title="Data da 1ª parcela ou pagamento à vista (quando o seguro passa a valer)"
                    >
                      1º Receb.
                    </th>
                  )}
                  {visiblePedidoCols.includes("confirmacao") && (
                    <th className={cn(listagemThCenter, "whitespace-nowrap px-1.5 py-2 w-[6%]")}>
                      Confirmação
                    </th>
                  )}
                  <th className={cn(listagemThCenter, "whitespace-nowrap px-1.5 py-2 w-[10%]")}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((pedido) => (
                  <tr key={pedido.id} className="border-b odd:bg-gray-50/60 hover:bg-gray-50">
                    {visiblePedidoCols.includes("numero") && (
                      <td className={cn(listagemTdCenter, "text-gray-900 font-semibold whitespace-nowrap px-1.5 py-2")}>
                      #{pedido.id.toString().padStart(6, '0')}
                      </td>
                    )}
                    {visiblePedidoCols.includes("cliente") && (
                      <td className={cn(listagemTdLeft, "text-gray-900 px-1.5 py-2")}>
                        <div className="font-medium break-words whitespace-normal">
                          {pedido.cliente?.nome || `Cliente ID: ${pedido.cliente_id}`}
                        </div>
                        <div className="text-[10px] text-gray-500 md:hidden break-words whitespace-normal">
                          {(pedido.cliente?.estado || "—").trim()} / {(pedido.cliente?.cidade || "—").trim()}
                        </div>
                      </td>
                    )}
                    {visiblePedidoCols.includes("localizacao") && (
                      <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell px-1.5 py-2")}>
                        <div className="break-words whitespace-normal">
                          {pedido.cliente?.estado || "—"} / {pedido.cliente?.cidade || "—"}
                        </div>
                      </td>
                    )}
                    {visiblePedidoCols.includes("criado_por") && (
                      <td className={cn(listagemTdCenter, "text-gray-600 hidden lg:table-cell px-1.5 py-2")}>
                        {(() => {
                          const nomeCompleto =
                            (pedido.vendedor_id || pedido.orcamento?.vendedor_id)
                              ? (userNamesMap?.[pedido.vendedor_id ?? ""] || userNamesMap?.[pedido.orcamento?.vendedor_id ?? ""] || "—")
                              : "—";
                          return (
                            <div className="truncate" title={nomeCompleto}>
                              {primeiroNome(nomeCompleto)}
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {visiblePedidoCols.includes("status") && (
                      <td className={cn(listagemTdCenter, "whitespace-nowrap px-1.5 py-2")}>
                        <div className="flex justify-center">
                        <Badge className={cn(getStatusListagemBadgeClass(pedido.tipo_negocio, pedido.numero_ciclo), "px-2 py-0.5 text-[11px]")}>
                          {getStatusListagemLabel(pedido.tipo_negocio, pedido.numero_ciclo)}
                        </Badge>
                        {pedido.pedido_anterior_id && (
                          <div className="text-[10px] text-gray-500 mt-0.5 text-center">
                            origem #{String(pedido.pedido_anterior_id).padStart(6, "0")}
                          </div>
                        )}
                        </div>
                      </td>
                    )}
                    {visiblePedidoCols.includes("data_renovacao") && (
                      <td className={cn(listagemTdCenter, "text-gray-700 hidden md:table-cell whitespace-nowrap px-1.5 py-2")}>
                        {(() => {
                          const dataRenov =
                            datasRenovacao[pedido.id] ||
                            pedido.data_renovacao ||
                            (pedido.tipo_negocio === "renovacao" ? pedido.data_pedido : null);
                          return dataRenov ? (
                            <span className="text-blue-700 font-medium">
                              {formatDataRenovacaoListagem(dataRenov)}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          );
                        })()}
                      </td>
                    )}
                    {visiblePedidoCols.includes("paineis") && (
                      <td className={cn(listagemTdCenter, "text-gray-900 font-semibold hidden md:table-cell px-1.5 py-2")}>
                        {typeof pedido.projeto_id === "number" ? (pedido.projeto?.total_paineis ?? 0) : "-"}
                      </td>
                    )}
                    {visiblePedidoCols.includes("plano") && (
                      <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell px-1.5 py-2")}>
                        {pedido.orcamento?.plano_id != null ? (
                          <div className="flex justify-center">
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] break-words whitespace-normal text-center leading-tight ${getPlanoColor(pedido.orcamento.plano_id, planos)}`}
                            >
                              {getPlanoNome(pedido.orcamento.plano_id, planos)}
                            </span>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                    )}
                    {visiblePedidoCols.includes("valor_projeto") && (
                      <td className={cn(listagemTdCenter, "text-gray-900 font-semibold hidden lg:table-cell whitespace-nowrap px-1.5 py-2")}>
                      {(() => {
                        // Valor do Projeto: Prioridade: Projeto > Orçamento > Pedido
                        if (pedido.projeto?.valor_projeto && pedido.projeto.valor_projeto > 0) {
                          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(pedido.projeto.valor_projeto));
                        }
                        if (pedido.orcamento?.valor_total) {
                          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(pedido.orcamento.valor_total));
                        }
                        if (pedido.valor_total) {
                          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(pedido.valor_total));
                        }
                        return '-';
                      })()}
                      </td>
                    )}
                    {visiblePedidoCols.includes("valor_seguro") && (
                      <td className={cn(listagemTdCenter, "text-gray-900 font-semibold hidden lg:table-cell whitespace-nowrap px-1.5 py-2")}>
                      {pedido.orcamento?.valor_seguro && pedido.orcamento.valor_seguro > 0
                        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(pedido.orcamento.valor_seguro))
                        : '-'
                      }
                      </td>
                    )}
                    {visiblePedidoCols.includes("data_aprovacao") && (
                      <td className={cn(listagemTdCenter, "text-gray-600 hidden md:table-cell whitespace-nowrap px-1.5 py-2")}>
                      {new Date(pedido.data_pedido).toLocaleDateString('pt-BR')}
                      </td>
                    )}
                    {visiblePedidoCols.includes("data_primeiro_recebimento") && (
                      <td className={cn(listagemTdCenter, "hidden md:table-cell whitespace-nowrap px-1.5 py-2")}>
                        {datasPrimeiroRecebimento[pedido.id] ? (
                          <span className="text-green-700 font-medium">
                            {formatDataPrimeiroRecebimento(pedido.id)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    )}
                    {visiblePedidoCols.includes("confirmacao") && (
                      <td className={cn(listagemTdCenter, "whitespace-nowrap px-1.5 py-2")}>
                        <div className="flex flex-col items-center gap-0.5">
                          <Badge className={cn(getConfirmacaoVendaBadgeClass(pedido.confirmacao_status), "px-2 py-0.5 text-[11px]")}>
                            {formatConfirmacaoVenda(pedido.confirmacao_status)}
                          </Badge>
                          {pedido.confirmacao_status === "confirmada" && pedido.confirmado_por && (
                            <span className="text-[10px] text-gray-500 truncate max-w-[90px]">
                              por {userNamesMap?.[pedido.confirmado_por] || "—"}
                            </span>
                          )}
                          {pedido.confirmacao_status === "cancelada" && pedido.confirmacao_cancelado_por && (
                            <span className="text-[10px] text-gray-500 truncate max-w-[90px]">
                              por {userNamesMap?.[pedido.confirmacao_cancelado_por] || "—"}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className={cn(listagemTdCenter, "px-1 py-2")}>
                      <div className="flex flex-nowrap gap-0.5 justify-center items-center whitespace-nowrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="h-7 w-7 p-0 shrink-0"
                          title="Ver Detalhes"
                          onClick={() => {
                            setPedidoDetalhesId(pedido.id);
                            setShowDetalhesPedido(true);
                          }}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {podeIniciarRenovacao(datasFimRenovacao[pedido.id]) &&
                          pedido.situacao_renovacao !== "renovado" &&
                          pedido.situacao_renovacao !== "nao_renovado" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 h-7 w-7 p-0 shrink-0"
                            title="Proposta de renovação"
                            onClick={() => setPedidoRenovacaoId(pedido.id)}
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {pedido.status === 'aprovado' && (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleCriarContrato(pedido)}
                              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 h-7 w-7 p-0 shrink-0"
                              title={
                                pedido.tipo_negocio === "renovacao" || Number(pedido.numero_ciclo) > 1
                                  ? "Atualizar contrato"
                                  : "Gerar Contrato"
                              }
                            >
                              <FileCheck className="h-3.5 w-3.5" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleCriarFinanceiro(pedido)}
                              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 h-7 w-7 p-0 shrink-0"
                              title="Gerar Financeiro"
                            >
                              <DollarSign className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {isAdmin() && pedido.confirmacao_status === "aguardando" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 h-7 w-7 p-0 shrink-0"
                              title="Confirmar venda"
                              onClick={async () => {
                                setComprovantePreview(null);
                                setPedidoConfirmar(pedido);
                                try {
                                  const { data: tx } = await supabase
                                    .from("transacoes_financeiras")
                                    .select("nota_fiscal_url, nota_fiscal_name, forma_pagamento, valor, numero_parcela")
                                    .eq("pedido_id", pedido.id)
                                    .eq("tipo", "entrada")
                                    .order("numero_parcela", { ascending: true })
                                    .limit(1)
                                    .maybeSingle();
                                  const { data: parc } = await supabase
                                    .from("parcelas")
                                    .select("comprovante_pagamento_url, comprovante_pagamento_name, forma_pagamento, valor")
                                    .eq("pedido_id", pedido.id)
                                    .eq("numero_parcela", 1)
                                    .limit(1)
                                    .maybeSingle();
                                  const url =
                                    tx?.nota_fiscal_url ||
                                    parc?.comprovante_pagamento_url ||
                                    null;
                                  const nome =
                                    tx?.nota_fiscal_name ||
                                    parc?.comprovante_pagamento_name ||
                                    "comprovante";
                                  if (url) setComprovantePreview({ url, nome });
                                  setPedidoConfirmar({
                                    ...pedido,
                                    _forma: tx?.forma_pagamento || parc?.forma_pagamento,
                                    _valor: tx?.valor || parc?.valor || pedido.orcamento?.valor_seguro,
                                  });
                                } catch {
                                  /* preview opcional */
                                }
                              }}
                            >
                              <CheckCircle className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 h-7 w-7 p-0 shrink-0"
                              title="Cancelar venda"
                              onClick={() => {
                                setMotivoCancelamento("");
                                setPedidoCancelar(pedido);
                              }}
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {currentItems.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nenhum pedido encontrado
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span className="text-gray-700">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <NovoPedidoDialog 
        open={showNovoPedido} 
        onOpenChange={setShowNovoPedido}
      />

      {/* Modal Criar Contrato */}
      <Dialog open={showNovoContrato} onOpenChange={(open) => {
        setShowNovoContrato(open);
        if (!open) {
          // Limpar preview do PDF ao fechar
          if (pdfPreviewUrl) {
            URL.revokeObjectURL(pdfPreviewUrl);
            setPdfPreviewUrl(null);
          }
          setArquivoContrato(null);
          setPdfGerado(null);
          setEtapaContrato('confirmar');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {etapaContrato === 'confirmar'
                ? (pedidoParaContrato?.tipo_negocio === "renovacao" || Number(pedidoParaContrato?.numero_ciclo) > 1
                    ? "Atualizar contrato da apólice"
                    : "Confirmar Dados do Contrato")
                : "Enviar Contrato Assinado"}
            </DialogTitle>
          </DialogHeader>
          
          {pedidoParaContrato && (
            <div className="space-y-4">
              {etapaContrato === 'confirmar' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Cliente</Label>
                      <Input 
                        value={pedidoParaContrato.cliente?.nome || `Cliente ID: ${pedidoParaContrato.cliente_id}`} 
                        disabled 
                      />
                    </div>
                    <div>
                      <Label>Valor do Seguro (Contrato)</Label>
                      <Input 
                        value={`R$ ${Number(pedidoParaContrato.valor_contrato || 0).toFixed(2).replace('.', ',')}`} 
                        disabled 
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Data de Início *</Label>
                      <Input 
                        type="date" 
                        value={pedidoParaContrato.data_inicio || new Date().toISOString().split('T')[0]}
                        onChange={async (e) => {
                      const novaDataInicio = e.target.value;
                      if (!novaDataInicio) return;
                      
                      const dataInicio = new Date(novaDataInicio);
                      const vigenciaAnos = pedidoParaContrato.vigencia_anos || 1;
                      const dataFim = new Date(dataInicio);
                      dataFim.setFullYear(dataFim.getFullYear() + vigenciaAnos);
                      dataFim.setDate(dataFim.getDate() - 1);
                      
                      console.log('Atualizando datas:', {
                        data_inicio: novaDataInicio,
                        vigencia_anos: vigenciaAnos,
                        data_fim: dataFim.toISOString().split('T')[0]
                      });
                      
                      const pedidoAtualizado = {
                        ...pedidoParaContrato,
                        data_inicio: novaDataInicio,
                        data_fim: dataFim.toISOString().split('T')[0]
                      };
                      
                      setPedidoParaContrato(pedidoAtualizado);
                      
                      // Regenerar PDF com novas datas
                      try {
                        const dadosContrato = {
                          cliente: pedidoParaContrato.cliente || {
                            nome: `Cliente ID: ${pedidoParaContrato.cliente_id}`,
                          },
                          equipamentos: pedidoParaContrato.orcamento?.equipamentos || 
                                       pedidoParaContrato.projeto?.equipamentos || [],
                          plano: pedidoParaContrato.orcamento?.plano || {
                            nome: pedidoParaContrato.orcamento?.plano_id || 'Não informado',
                            vigencia_anos: pedidoParaContrato.orcamento?.vigencia_anos || 1,
                          },
                          orcamento: {
                            quantidade_placas: pedidoParaContrato.orcamento?.quantidade_placas || 0,
                            valor_seguro: pedidoParaContrato.orcamento?.valor_seguro || 0,
                            valor_total: pedidoParaContrato.orcamento?.valor_total || 0,
                            forma_pagamento: pedidoParaContrato.orcamento?.forma_pagamento,
                          },
                          projeto: pedidoParaContrato.projeto || {},
                          contrato: {
                            data_inicio: novaDataInicio,
                            data_fim: dataFim.toISOString().split('T')[0],
                            valor: pedidoParaContrato.valor_contrato || 0,
                          },
                        };

                        const pdfBlob = await preencherPdfContrato(dadosContrato);
                        const pdfFile = new File([pdfBlob], `${pedidoParaContrato.cliente?.nome?.replace(/[^a-zA-Z0-9]/g, '_') || 'CONTRATO'}_${new Date().toISOString().split('T')[0]}.pdf`, {
                          type: 'application/pdf',
                        });
                        
                        // Atualizar preview
                        if (pdfPreviewUrl) {
                          URL.revokeObjectURL(pdfPreviewUrl);
                        }
                        const novaUrl = URL.createObjectURL(pdfBlob);
                        setPdfPreviewUrl(novaUrl);
                        setArquivoContrato(pdfFile);
                      } catch (error) {
                        console.error('Erro ao regenerar PDF:', error);
                        toast.warning("Erro ao atualizar preview do PDF");
                      }
                    }}
                  />
                </div>
                <div>
                  <Label>Vigência (anos)</Label>
                  <Input 
                    value={pedidoParaContrato.vigencia_anos || 1}
                    disabled
                  />
                </div>
                <div>
                  <Label>Data de Fim (Sugestão)</Label>
                  <Input 
                    type="date"
                    value={pedidoParaContrato.data_fim}
                    onChange={(e) => setPedidoParaContrato({
                      ...pedidoParaContrato,
                      data_fim: e.target.value
                    })}
                  />
                </div>
              </div>
              


                    <div>
                      <Label>Observações</Label>
                      <Input 
                        placeholder="Observações sobre o contrato..."
                        onChange={(e) => setPedidoParaContrato({...pedidoParaContrato, observacoes: e.target.value})}
                      />
                    </div>

                    {/* Preview do PDF */}
                    {pdfPreviewUrl && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <Label className="mb-2 block">Pré-visualização do Contrato</Label>
                        <iframe
                          src={pdfPreviewUrl}
                          className="w-full h-96 border rounded"
                          title="Preview do Contrato"
                        />
                      </div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800 mb-2">
                        <strong>📋 Revise o contrato acima:</strong> Verifique se todas as informações estão corretas antes de fazer o download.
                      </p>
                      <p className="text-sm text-blue-800">
                        <strong>Próximo passo:</strong> Faça o download do contrato, assine digitalmente e depois faça o upload do arquivo assinado.
                      </p>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => {
                        setShowNovoContrato(false);
                        if (pdfPreviewUrl) {
                          URL.revokeObjectURL(pdfPreviewUrl);
                          setPdfPreviewUrl(null);
                        }
                        setArquivoContrato(null);
                        setPdfGerado(null);
                        setPedidoParaContrato(null);
                        setEtapaContrato('confirmar');
                      }}>
                        Cancelar
                      </Button>
                      <Button 
                        onClick={async () => {
                          try {
                            // Validações
                            if (!pedidoParaContrato.data_inicio) {
                              toast.error("Data de início é obrigatória");
                              return;
                            }
                            
                            if (!pedidoParaContrato.data_fim) {
                              toast.error("Data de fim é obrigatória");
                              return;
                            }
                            
                            const dataInicio = new Date(pedidoParaContrato.data_inicio);
                            const dataFim = new Date(pedidoParaContrato.data_fim);
                            
                            if (dataFim <= dataInicio) {
                              toast.error("A data de fim deve ser posterior à data de início");
                              return;
                            }
                            
                            // Regenerar PDF com as datas atualizadas
                            const dadosContrato = {
                              cliente: pedidoParaContrato.cliente || {
                                nome: `Cliente ID: ${pedidoParaContrato.cliente_id}`,
                              },
                              equipamentos: pedidoParaContrato.orcamento?.equipamentos || 
                                           pedidoParaContrato.projeto?.equipamentos || [],
                              plano: pedidoParaContrato.orcamento?.plano || {
                                nome: pedidoParaContrato.orcamento?.plano_id || 'Não informado',
                                vigencia_anos: pedidoParaContrato.orcamento?.vigencia_anos || 1,
                              },
                              orcamento: {
                                quantidade_placas: pedidoParaContrato.orcamento?.quantidade_placas || 0,
                                valor_seguro: pedidoParaContrato.orcamento?.valor_seguro || 0,
                                valor_total: pedidoParaContrato.orcamento?.valor_total || 0,
                                forma_pagamento: pedidoParaContrato.orcamento?.forma_pagamento,
                              },
                              projeto: pedidoParaContrato.projeto || {},
                              contrato: {
                                data_inicio: pedidoParaContrato.data_inicio,
                                data_fim: pedidoParaContrato.data_fim,
                                valor: pedidoParaContrato.valor_contrato || 0,
                              },
                            };
                            
                            const pdfParaDownload = await preencherPdfContrato(dadosContrato);
                            
                            // Fazer download do PDF
                            const url = URL.createObjectURL(pdfParaDownload);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${pedidoParaContrato.cliente?.nome?.replace(/[^a-zA-Z0-9]/g, '_') || 'CONTRATO'}_${new Date().toISOString().split('T')[0]}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                            
                            // Atualizar PDF gerado com as datas corretas
                            setPdfGerado(pdfParaDownload);
                            
                            // Avançar para etapa de upload
                            setEtapaContrato('upload');
                            toast.success("PDF baixado com sucesso! Agora faça o upload do contrato assinado.");
                          } catch (error) {
                            console.error('Erro ao fazer download do PDF:', error);
                            toast.error(`Erro ao fazer download: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
                          }
                        }}
                        className="bg-primary hover:bg-primary/90"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Fazer Download para Assinar
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-green-800">
                        <strong>✓ Download realizado!</strong> Agora faça o upload do contrato assinado digitalmente.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Cliente</Label>
                        <Input 
                          value={pedidoParaContrato.cliente?.nome || `Cliente ID: ${pedidoParaContrato.cliente_id}`} 
                          disabled 
                        />
                      </div>
                      <div>
                        <Label>Valor do Seguro (Contrato)</Label>
                        <Input 
                          value={`R$ ${Number(pedidoParaContrato.valor_contrato || 0).toFixed(2).replace('.', ',')}`} 
                          disabled 
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Data de Início</Label>
                        <Input 
                          value={new Date(pedidoParaContrato.data_inicio).toLocaleDateString('pt-BR')} 
                          disabled 
                        />
                      </div>
                      <div>
                        <Label>Data de Fim</Label>
                        <Input 
                          value={new Date(pedidoParaContrato.data_fim).toLocaleDateString('pt-BR')} 
                          disabled 
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Enviar Contrato Assinado (PDF) <span className="text-gray-500 text-xs">(opcional - pode anexar depois)</span></Label>
                      <FileInput
                        label=""
                        accept=".pdf"
                        onFileSelect={(file) => {
                          setArquivoContrato(file);
                          if (file) {
                            const url = URL.createObjectURL(file);
                            if (pdfPreviewUrl) {
                              URL.revokeObjectURL(pdfPreviewUrl);
                            }
                            setPdfPreviewUrl(url);
                          }
                        }}
                        selectedFile={arquivoContrato}
                        placeholder="Selecionar contrato assinado..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Você pode anexar o contrato assinado agora ou depois na listagem de contratos.
                      </p>
                    </div>

                    {pdfPreviewUrl && arquivoContrato && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <Label className="mb-2 block">Preview do Contrato Assinado</Label>
                        <iframe
                          src={pdfPreviewUrl}
                          className="w-full h-96 border rounded"
                          title="Preview do Contrato Assinado"
                        />
                      </div>
                    )}

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => {
                        setEtapaContrato('confirmar');
                        setArquivoContrato(null);
                        if (pdfPreviewUrl) {
                          URL.revokeObjectURL(pdfPreviewUrl);
                          setPdfPreviewUrl(null);
                        }
                      }}>
                        Voltar
                      </Button>
                      <Button 
                        onClick={async () => {
                          try {
                            console.log('Criando contrato:', {
                              cliente_id: pedidoParaContrato.cliente_id,
                              pedido_id: pedidoParaContrato.id,
                              valor: pedidoParaContrato.valor_contrato || 0,
                              data_inicio: pedidoParaContrato.data_inicio,
                              data_fim: pedidoParaContrato.data_fim,
                              observacoes: pedidoParaContrato.observacoes
                            });
                            
                            // 1. Criar o contrato
                            const isRenovacaoContrato =
                              pedidoParaContrato.tipo_negocio === "renovacao" ||
                              Number(pedidoParaContrato.numero_ciclo) > 1;
                            const contratoCriado = await criarContrato.mutateAsync({
                              cliente_id: Number(pedidoParaContrato.cliente_id),
                              pedido_id: Number(pedidoParaContrato.id),
                              valor: Number(pedidoParaContrato.valor_contrato) || 0,
                              data_inicio: pedidoParaContrato.data_inicio,
                              data_fim: pedidoParaContrato.data_fim,
                              observacoes: pedidoParaContrato.observacoes
                            });
                            
                            console.log('Contrato criado:', contratoCriado);
                            
                            // 2. Fazer upload do arquivo assinado (se fornecido)
                            if (arquivoContrato) {
                              try {
                                await uploadArquivoContrato(arquivoContrato, contratoCriado.id);
                                console.log('Arquivo do contrato assinado enviado com sucesso');
                                toast.success(
                                  isRenovacaoContrato
                                    ? "Contrato atualizado com arquivo anexado!"
                                    : "Contrato criado com arquivo anexado!"
                                );
                              } catch (uploadError) {
                                console.error('Erro ao fazer upload do arquivo:', uploadError);
                                toast.warning(
                                  isRenovacaoContrato
                                    ? "Contrato atualizado, mas houve erro ao enviar o arquivo assinado."
                                    : "Contrato criado, mas houve erro ao enviar o arquivo assinado. Você pode anexar depois na listagem de contratos."
                                );
                              }
                            } else {
                              toast.success(
                                isRenovacaoContrato
                                  ? "Contrato atualizado. Você pode anexar o aditivo assinado depois na listagem."
                                  : "Contrato criado! Você pode anexar o arquivo assinado depois na listagem de contratos."
                              );
                            }

                            if (!isRenovacaoContrato) {
                            try {
                              // Buscar forma de pagamento (nome + parcelas_max do cadastro)
                              let formaPagamentoNome = 'PIX';
                              let formaTipo: string | null = null;
                              let formaParcelasMax: number | null = null;
                              if (pedidoParaContrato.orcamento?.forma_pagamento) {
                                const { data: formaPagamento } = await supabase
                                  .from('formas_pagamento')
                                  .select('nome, tipo, parcelas_max')
                                  .eq('id', pedidoParaContrato.orcamento.forma_pagamento)
                                  .single();
                                
                                if (formaPagamento?.nome) {
                                  formaPagamentoNome = formaPagamento.nome;
                                }
                                formaTipo = formaPagamento?.tipo ?? null;
                                formaParcelasMax = formaPagamento?.parcelas_max ?? null;
                              }

                              // Não-cartão: usa parcelas_max do cadastro (ex.: Boleto 3X → 3),
                              // mesmo se o orçamento antigo tiver parcelas_cartao=1.
                              const numeroParcelas = resolverNumeroParcelasGeracao({
                                numeroParcelasOrcamento: pedidoParaContrato.orcamento?.numero_parcelas,
                                parcelasCartaoOrcamento: pedidoParaContrato.orcamento?.parcelas_cartao,
                                parcelasMaxForma: formaParcelasMax,
                                isCartao: formaTipo === 'cartao',
                                formaNome: formaPagamentoNome,
                              });
                              
                              console.log('✅ Número de parcelas final para contrato:', {
                                numero_parcelas: pedidoParaContrato.orcamento?.numero_parcelas,
                                parcelas_cartao: pedidoParaContrato.orcamento?.parcelas_cartao,
                                forma_pagamento: pedidoParaContrato.orcamento?.forma_pagamento,
                                formaPagamentoNome,
                                formaParcelasMax,
                                numeroParcelas,
                              });
                              
                              await gerarParcelasContrato(
                                contratoCriado.id,
                                Number(pedidoParaContrato.cliente_id),
                                Number(pedidoParaContrato.id),
                                Number(pedidoParaContrato.valor_contrato) || 0,
                                formaPagamentoNome,
                                pedidoParaContrato.data_inicio,
                                numeroParcelas,
                                pedidoParaContrato.orcamento?.forma_pagamento
                              );
                              
                              console.log('Parcelas geradas automaticamente');
                              toast.success("Parcelas geradas automaticamente!");
                            } catch (parcelasError) {
                              console.error('Erro ao gerar parcelas:', parcelasError);
                              toast.warning("Contrato criado, mas houve erro ao gerar parcelas. Você pode criar manualmente depois.");
                            }
                            }

                      // Renovação na agenda será criada ao receber a primeira parcela no Financeiro (não ao gerar contrato)
                            toast.success(
                              isRenovacaoContrato
                                ? "Contrato atualizado. Use Gerar Financeiro no pedido desta venda."
                                : "Contrato criado com sucesso! Redirecionando para a listagem de contratos..."
                            );
                            
                            // Limpar estados
                            setShowNovoContrato(false);
                            setPedidoParaContrato(null);
                            if (pdfPreviewUrl) {
                              URL.revokeObjectURL(pdfPreviewUrl);
                              setPdfPreviewUrl(null);
                            }
                            setArquivoContrato(null);
                            setPdfGerado(null);
                            setEtapaContrato('confirmar');
                            
                            // Aguardar um pouco antes de redirecionar para garantir que o toast seja visível
                            setTimeout(() => {
                              // Redirecionar para a listagem de contratos
                              window.dispatchEvent(new CustomEvent('mudar_secao', { detail: 'contratos' }));
                            }, 500);
                          } catch (error) {
                            console.error('Erro detalhado ao criar contrato:', error);
                            console.error('Dados enviados:', {
                              cliente_id: Number(pedidoParaContrato.cliente_id),
                              pedido_id: Number(pedidoParaContrato.id),
                              valor: Number(pedidoParaContrato.valor_contrato) || 0,
                              data_inicio: pedidoParaContrato.data_inicio,
                              data_fim: pedidoParaContrato.data_fim,
                              observacoes: pedidoParaContrato.observacoes
                            });
                            toast.error("Erro ao criar contrato: " + (error as any)?.message);
                          }
                        }}
                        className="bg-primary hover:bg-primary/90"
                      >
                        Criar Contrato
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
        </DialogContent>
      </Dialog>

      {/* Modal Criar Financeiro */}
      <Dialog open={showNovoFinanceiro} onOpenChange={(open) => {
        setShowNovoFinanceiro(open);
        if (!open) {
          setPedidoParaFinanceiro(null);
          setNumeroParcelasFinanceiro(1);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Criar Transação Financeira</DialogTitle>
          </DialogHeader>
          
          {pedidoParaFinanceiro && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cliente</Label>
                  <Input 
                    value={pedidoParaFinanceiro.cliente?.nome || `Cliente ID: ${pedidoParaFinanceiro.cliente_id}`} 
                    disabled 
                  />
                </div>
                <div>
                  <Label>Valor do Seguro (Financeiro)</Label>
                  <Input 
                    value={`R$ ${Number(pedidoParaFinanceiro.valor_financeiro || 0).toFixed(2).replace('.', ',')}`} 
                    disabled 
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Forma de Pagamento *</Label>
                  <Select 
                    value={pedidoParaFinanceiro.forma_pagamento || ''}
                    onValueChange={(value) => {
                      setPedidoParaFinanceiro({...pedidoParaFinanceiro, forma_pagamento: value});
                      const forma = formasPagamento.find((f: { nome: string; tipo?: string; parcelas_max?: number }) => f.nome === value);
                      const max = Math.max(1, Number(forma?.parcelas_max) || 1);
                      const isCartao = forma?.tipo === 'cartao'
                        || value.toLowerCase().includes('cartão')
                        || value.toLowerCase().includes('cartao');
                      // Não-cartão: quantidade = cadastro (ex.: Boleto 3X → 3)
                      if (!isCartao) {
                        setNumeroParcelasFinanceiro(max);
                      } else {
                        setNumeroParcelasFinanceiro((atual: number) => Math.min(Math.max(1, atual), max));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a forma de pagamento" />
                    </SelectTrigger>
                    <SelectContent>
                      {formasPagamento.length > 0 ? (
                        formasPagamento.map((forma) => (
                          <SelectItem key={forma.id} value={forma.nome}>
                            {forma.nome}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="pix" disabled>Nenhuma forma de pagamento cadastrada</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Número de Parcelas *</Label>
                  <Input 
                    type="number" 
                    min="1"
                    max={(() => {
                      const forma = formasPagamento.find((f: { nome: string; tipo?: string; parcelas_max?: number }) => f.nome === pedidoParaFinanceiro.forma_pagamento);
                      const fp = (pedidoParaFinanceiro.forma_pagamento || '').toLowerCase();
                      if (fp.includes('pix') || fp.includes('à vista') || fp.includes('avista')) return 1;
                      return Math.max(1, Number(forma?.parcelas_max) || 1);
                    })()}
                    value={numeroParcelasFinanceiro}
                    onChange={(e) => {
                      const forma = formasPagamento.find((f: { nome: string; tipo?: string; parcelas_max?: number }) => f.nome === pedidoParaFinanceiro.forma_pagamento);
                      const formaLower = (pedidoParaFinanceiro.forma_pagamento || '').toLowerCase();
                      const max = formaLower.includes('pix') || formaLower.includes('à vista') || formaLower.includes('avista') ? 1 : Math.max(1, Number(forma?.parcelas_max) || 1);
                      const valor = Math.min(max, Math.max(1, parseInt(e.target.value) || 1));
                      setNumeroParcelasFinanceiro(valor);
                    }}
                    disabled={
                      !pedidoParaFinanceiro.forma_pagamento || 
                      pedidoParaFinanceiro.forma_pagamento.toLowerCase().includes('pix') ||
                      pedidoParaFinanceiro.forma_pagamento.toLowerCase().includes('à vista') ||
                      pedidoParaFinanceiro.forma_pagamento.toLowerCase().includes('avista')
                    }
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {pedidoParaFinanceiro.forma_pagamento && 
                     (pedidoParaFinanceiro.forma_pagamento.toLowerCase().includes('pix') ||
                      pedidoParaFinanceiro.forma_pagamento.toLowerCase().includes('à vista') ||
                      pedidoParaFinanceiro.forma_pagamento.toLowerCase().includes('avista'))
                      ? 'Forma de pagamento à vista - apenas 1 parcela'
                      : (() => {
                          const forma = formasPagamento.find((f: { nome: string; tipo?: string; parcelas_max?: number }) => f.nome === pedidoParaFinanceiro.forma_pagamento);
                          const max = Math.max(1, Number(forma?.parcelas_max) || 1);
                          return `Digite o número de parcelas desejado (1-${max})`;
                        })()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data de Vencimento (Primeira Parcela) *</Label>
                  <Input 
                    type="date" 
                    value={pedidoParaFinanceiro.data_vencimento || ''}
                    onChange={(e) => setPedidoParaFinanceiro({...pedidoParaFinanceiro, data_vencimento: e.target.value})}
                  />
                </div>
                {numeroParcelasFinanceiro > 1 ? (
                  <div>
                    <Label>Valor por Parcela</Label>
                    {(() => {
                      const valorBruto = Number(pedidoParaFinanceiro.valor_financeiro || 0);
                      const forma = formasPagamento.find((f: { nome: string; tipo?: string; taxa_maquina_cartao?: number }) => f.nome === pedidoParaFinanceiro.forma_pagamento);
                      const taxa = (forma?.tipo === 'cartao' ? Number(forma?.taxa_maquina_cartao) || 0 : 0);
                      const descontoTaxa = valorBruto * (taxa / 100);
                      const valorLiquido = Math.round((valorBruto - descontoTaxa) * 100) / 100;
                      const valorParcela = valorLiquido / numeroParcelasFinanceiro;
                      return (
                        <>
                          <Input 
                            value={`R$ ${valorParcela.toFixed(2).replace('.', ',')}`}
                            disabled
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {taxa > 0 ? (
                              <>Valor líquido R$ {valorLiquido.toFixed(2).replace('.', ',')} (após taxa máquina {taxa.toFixed(2).replace('.', ',')}% = R$ {descontoTaxa.toFixed(2).replace('.', ',')}) → {numeroParcelasFinanceiro}x de R$ {valorParcela.toFixed(2).replace('.', ',')}</>
                            ) : (
                              <>{numeroParcelasFinanceiro}x de R$ {valorParcela.toFixed(2).replace('.', ',')}</>
                            )}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                ) : (() => {
                  const forma = formasPagamento.find((f: { nome: string; tipo?: string; taxa_maquina_cartao?: number }) => f.nome === pedidoParaFinanceiro.forma_pagamento);
                  const taxa = (forma?.tipo === 'cartao' ? Number(forma?.taxa_maquina_cartao) || 0 : 0);
                  if (taxa > 0) {
                    const valorBruto = Number(pedidoParaFinanceiro.valor_financeiro || 0);
                    const descontoTaxa = valorBruto * (taxa / 100);
                    const valorLiquido = Math.round((valorBruto - descontoTaxa) * 100) / 100;
                    return (
                      <div>
                        <Label>Valor à vista (após taxa)</Label>
                        <p className="text-sm text-gray-600">
                          Valor líquido R$ {valorLiquido.toFixed(2).replace('.', ',')} <span className="text-gray-500">(após taxa máquina {taxa.toFixed(2).replace('.', ',')}% = R$ {descontoTaxa.toFixed(2).replace('.', ',')})</span>
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              <div>
                <Label>Observações</Label>
                <Input
                  placeholder="Observações sobre a transação..."
                  value={pedidoParaFinanceiro.observacoes || ""}
                  onChange={(e) => setPedidoParaFinanceiro({...pedidoParaFinanceiro, observacoes: e.target.value})}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNovoFinanceiro(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={async () => {
                    try {
                      // Validações
                      if (!pedidoParaFinanceiro.forma_pagamento) {
                        toast.error("Forma de pagamento é obrigatória");
                        return;
                      }
                      
                      if (!pedidoParaFinanceiro.data_vencimento) {
                        toast.error("Data de vencimento é obrigatória");
                        return;
                      }

                      const formaAtivaValidacao = formasPagamento.find((f: { nome: string; tipo?: string; parcelas_max?: number }) => f.nome === pedidoParaFinanceiro.forma_pagamento);
                      const formaLowerValidacao = (pedidoParaFinanceiro.forma_pagamento || '').toLowerCase();
                      const maxParcelasPermitido = formaLowerValidacao.includes('pix') || formaLowerValidacao.includes('à vista') || formaLowerValidacao.includes('avista')
                        ? 1
                        : Math.max(1, Number(formaAtivaValidacao?.parcelas_max) || 1);
                      const parcelasFinaisParaValidacao = (formaLowerValidacao.includes('pix') || formaLowerValidacao.includes('à vista') || formaLowerValidacao.includes('avista'))
                        ? 1
                        : numeroParcelasFinanceiro;
                      if (parcelasFinaisParaValidacao > maxParcelasPermitido) {
                        toast.error(`Esta forma de pagamento permite no máximo ${maxParcelasPermitido} parcela(s).`);
                        return;
                      }

                      const valorTotal = Number(pedidoParaFinanceiro.valor_financeiro) || 0;
                      const formaLower = pedidoParaFinanceiro.forma_pagamento.toLowerCase();
                      const parcelasFinais = (formaLower.includes('pix') || 
                                             formaLower.includes('à vista') || 
                                             formaLower.includes('avista')) 
                                             ? 1 
                                             : numeroParcelasFinanceiro;

                      // Se cartão, descontar a taxa percentual da máquina do valor antes de parcelar/criar transações
                      const formaAtiva = formasPagamento.find((f: { nome: string; tipo?: string; taxa_maquina_cartao?: number }) => f.nome === pedidoParaFinanceiro.forma_pagamento);
                      const taxaMaquinaCartao = (formaAtiva?.tipo === 'cartao' ? Number(formaAtiva?.taxa_maquina_cartao) || 0 : 0);
                      const descontoTaxa = valorTotal * (taxaMaquinaCartao / 100);
                      const valorParaParcelas = Math.round((valorTotal - descontoTaxa) * 100) / 100;

                      // Sempre criar transações financeiras para aparecer na seção Financeiro (independente de ter contrato)
                      if (parcelasFinais > 1) {
                        // Parcelado: N transações
                        const valorBase = Math.round((valorParaParcelas / parcelasFinais) * 100) / 100;
                        let dataVenc = proximoDiaUtil(parseDateLocal(pedidoParaFinanceiro.data_vencimento));

                        for (let i = 1; i <= parcelasFinais; i++) {
                          const isUltima = i === parcelasFinais;
                          const valor = isUltima
                            ? Math.round((valorParaParcelas - valorBase * (parcelasFinais - 1)) * 100) / 100
                            : valorBase;
                          const dataVencStr = toDateStringLocal(dataVenc);

                          await criarTransacao.mutateAsync({
                            pedido_id: Number(pedidoParaFinanceiro.id),
                            cliente_id: Number(pedidoParaFinanceiro.cliente_id),
                            tipo: 'entrada',
                            valor,
                            forma_pagamento: pedidoParaFinanceiro.forma_pagamento,
                            data_vencimento: dataVencStr,
                            numero_parcela: i,
                            total_parcelas: parcelasFinais,
                            observacoes: pedidoParaFinanceiro.observacoes?.trim()
                              ? `${pedidoParaFinanceiro.observacoes.trim()} (${i}/${parcelasFinais})`
                              : undefined
                          });

                          if (i < parcelasFinais) {
                            dataVenc = new Date(dataVenc.getFullYear(), dataVenc.getMonth(), dataVenc.getDate());
                            dataVenc.setDate(dataVenc.getDate() + 30);
                            dataVenc = proximoDiaUtil(dataVenc);
                          }
                        }

                        toast.success(`${parcelasFinais} transações criadas com sucesso! Verifique na seção Financeiro.`);
                      } else {
                        // À vista: uma transação
                        const dataVencUtil = toDateStringLocal(proximoDiaUtil(parseDateLocal(pedidoParaFinanceiro.data_vencimento)));
                        await criarTransacao.mutateAsync({
                          pedido_id: Number(pedidoParaFinanceiro.id),
                          cliente_id: Number(pedidoParaFinanceiro.cliente_id),
                          tipo: 'entrada',
                          valor: valorParaParcelas,
                          forma_pagamento: pedidoParaFinanceiro.forma_pagamento,
                          data_vencimento: dataVencUtil,
                          numero_parcela: 1,
                          total_parcelas: 1,
                          observacoes: pedidoParaFinanceiro.observacoes?.trim() || undefined
                        });

                        toast.success("Transação financeira criada com sucesso! Verifique na seção Financeiro.");
                      }

                      await marcarPedidoAguardandoConfirmacao(Number(pedidoParaFinanceiro.id));
                      await queryClient.invalidateQueries({ queryKey: ["pedidos"] });
                      toast.info("Venda aguardando confirmação da gerência.");
                      
                      setShowNovoFinanceiro(false);
                      setPedidoParaFinanceiro(null);
                      setNumeroParcelasFinanceiro(1);
                    } catch (error) {
                      console.error('Erro detalhado ao criar transação:', error);
                      toast.error(`Erro ao criar transação financeira: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
                    }
                  }}
                  disabled={criarTransacao.isPending}
                >
                  {criarTransacao.isPending ? "Criando..." : "Criar Transação"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Detalhes do Pedido */}
      <DetalhesPedidoDialog
        open={showDetalhesPedido}
        onOpenChange={(open) => {
          setShowDetalhesPedido(open);
          if (!open) setPedidoDetalhesId(null);
        }}
        pedidoId={pedidoDetalhesId}
      />

      <CriarPedidoRenovacaoDialog
        open={pedidoRenovacaoId != null}
        onOpenChange={(open) => {
          if (!open) setPedidoRenovacaoId(null);
        }}
        pedidoAnteriorId={pedidoRenovacaoId}
      />

      <Dialog
        open={!!pedidoConfirmar}
        onOpenChange={(open) => {
          if (!open) {
            setPedidoConfirmar(null);
            setComprovantePreview(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmar venda</DialogTitle>
          </DialogHeader>
          {pedidoConfirmar && (
            <div className="space-y-3 text-sm">
              <p>
                <span className="text-gray-600">Pedido:</span>{" "}
                <strong>#{String(pedidoConfirmar.id).padStart(6, "0")}</strong>
              </p>
              <p>
                <span className="text-gray-600">Cliente:</span>{" "}
                {pedidoConfirmar.cliente?.nome || `ID ${pedidoConfirmar.cliente_id}`}
              </p>
              <p>
                <span className="text-gray-600">Valor:</span>{" "}
                {pedidoConfirmar._valor != null
                  ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
                      Number(pedidoConfirmar._valor)
                    )
                  : "—"}
              </p>
              <p>
                <span className="text-gray-600">Forma:</span>{" "}
                {pedidoConfirmar._forma || "—"}
              </p>
              {comprovantePreview ? (
                <div className="rounded border bg-gray-50 p-3 space-y-2">
                  <p className="font-medium text-gray-800">Comprovante anexado</p>
                  <p className="text-xs text-gray-600 truncate">{comprovantePreview.nome}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void openStorageUrl(comprovantePreview.url)}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Abrir comprovante
                  </Button>
                </div>
              ) : (
                <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs">
                  Sem comprovante anexado. Você ainda pode confirmar após o depara com o extrato.
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPedidoConfirmar(null);
                    setComprovantePreview(null);
                  }}
                >
                  Voltar
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={confirmarVenda.isPending}
                  onClick={() => {
                    confirmarVenda.mutate(Number(pedidoConfirmar.id), {
                      onSuccess: () => {
                        toast.success("Venda confirmada com sucesso!");
                        setPedidoConfirmar(null);
                        setComprovantePreview(null);
                      },
                      onError: (e) =>
                        toast.error(e instanceof Error ? e.message : "Erro ao confirmar"),
                    });
                  }}
                >
                  {confirmarVenda.isPending ? "Confirmando..." : "Confirmar venda"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pedidoCancelar}
        onOpenChange={(open) => {
          if (!open) {
            setPedidoCancelar(null);
            setMotivoCancelamento("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar venda?</AlertDialogTitle>
            <AlertDialogDescription>
              O pedido #{pedidoCancelar ? String(pedidoCancelar.id).padStart(6, "0") : ""} será
              cancelado e as cobranças pendentes serão canceladas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="motivo-cancelamento-venda">Motivo *</Label>
            <Textarea
              id="motivo-cancelamento-venda"
              value={motivoCancelamento}
              onChange={(e) => setMotivoCancelamento(e.target.value)}
              placeholder="Informe o motivo do cancelamento"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={cancelarConfirmacao.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!pedidoCancelar) return;
                cancelarConfirmacao.mutate(
                  { pedidoId: Number(pedidoCancelar.id), motivo: motivoCancelamento },
                  {
                    onSuccess: () => {
                      toast.success("Venda cancelada.");
                      setPedidoCancelar(null);
                      setMotivoCancelamento("");
                    },
                    onError: (err) =>
                      toast.error(err instanceof Error ? err.message : "Erro ao cancelar"),
                  }
                );
              }}
            >
              {cancelarConfirmacao.isPending ? "Cancelando..." : "Cancelar venda"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}